import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from './firebase';

class FirebaseService {
  /**
   * Sign in with email and password
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise<UserCredential>}
   */
  async signIn(email, password) {
    try {
      console.log('🔐 Attempting sign in...');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✓ Sign in successful');
      return userCredential;
    } catch (error) {
      console.error('✗ Sign in failed:', error.code);
      throw this._handleAuthError(error);
    }
  }

  /**
   * Create new user account
   * @param {string} email 
   * @param {string} password 
   * @returns {Promise<UserCredential>}
   */
  async createAccount(email, password) {
    try {
      console.log('📝 Creating new account...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('✓ Account created successfully');
      return userCredential;
    } catch (error) {
      console.error('✗ Account creation failed:', error.code);
      throw this._handleAuthError(error);
    }
  }

  /**
   * Sign out current user
   * @returns {Promise<void>}
   */
  async signOut() {
    try {
      console.log('🚪 Signing out...');
      await signOut(auth);
      console.log('✓ Sign out successful');
    } catch (error) {
      console.error('✗ Sign out failed:', error);
      throw new Error('Failed to sign out');
    }
  }

  /**
   * Send password reset email
   * @param {string} email 
   * @returns {Promise<void>}
   */
  async sendPasswordReset(email) {
    try {
      console.log('📧 Sending password reset email...');
      await sendPasswordResetEmail(auth, email);
      console.log('✓ Password reset email sent');
    } catch (error) {
      console.error('✗ Password reset failed:', error.code);
      throw this._handleAuthError(error);
    }
  }

  /**
   * Subscribe to authentication state changes
   * @param {Function} callback - Called with user object or null
   * @returns {Function} Unsubscribe function
   */
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('✓ Auth state: User signed in -', user.email);
      } else {
        console.log('○ Auth state: No user');
      }
      callback(user);
    });
  }

  /**
   * Get current user's ID token
   * @param {boolean} forceRefresh - Force token refresh
   * @returns {Promise<string|null>} ID token or null
   */
  async getIdToken(forceRefresh = false) {
    const user = auth.currentUser;
    
    if (!user) {
      console.warn('⚠ No authenticated user');
      return null;
    }

    try {
      const token = await user.getIdToken(forceRefresh);
      console.log('✓ ID token retrieved');
      return token;
    } catch (error) {
      console.error('✗ Failed to get ID token:', error);
      throw new Error('Failed to retrieve authentication token');
    }
  }

  /**
   * Get current authenticated user
   * @returns {User|null}
   */
  getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Wait for auth to be ready
   * @param {number} timeout - Timeout in milliseconds
   * @returns {Promise<User|null>}
   */
  async waitForAuth(timeout = 5000) {
    return new Promise((resolve) => {
      // If there's already a user, resolve immediately
      if (auth.currentUser) {
        resolve(auth.currentUser);
        return;
      }

      // Otherwise, wait for auth state to initialize
      const timeoutId = setTimeout(() => {
        unsubscribe();
        console.warn('⚠ Auth initialization timeout');
        resolve(null);
      }, timeout);

      const unsubscribe = auth.onAuthStateChanged((user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        resolve(user);
      });
    });
  }

  /**
   * Handle Firebase auth errors and convert to user-friendly messages
   * @private
   */
  _handleAuthError(error) {
    const errorMessages = {
      'auth/invalid-email': 'Invalid email address',
      'auth/user-disabled': 'This account has been disabled',
      'auth/user-not-found': 'No account found with this email',
      'auth/wrong-password': 'Incorrect password',
      'auth/email-already-in-use': 'An account already exists with this email',
      'auth/weak-password': 'Password should be at least 6 characters',
      'auth/network-request-failed': 'Network error. Please check your connection',
      'auth/too-many-requests': 'Too many attempts. Please try again later',
    };

    const message = errorMessages[error.code] || 'Authentication failed. Please try again.';
    return new Error(message);
  }
}

// Export singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;
